import { useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type OnboardingFlow = "school" | "learner" | "merchant" | "parent";

export type OnboardingRecord = {
  id: string;
  flow: OnboardingFlow;
  entity_id: string | null;
  current_step: number;
  total_steps: number;
  state: Record<string, unknown>;
  completed: boolean;
};

async function fetchProgress(flow: OnboardingFlow, entityId?: string | null) {
  const q = supabase.from("onboarding_progress" as never).select("*").eq("flow", flow);
  const { data } = entityId
    ? await q.eq("entity_id", entityId).maybeSingle()
    : await q.is("entity_id", null).maybeSingle();
  return (data ?? null) as OnboardingRecord | null;
}

export function useOnboardingProgress(flow: OnboardingFlow, entityId?: string | null) {
  const qc = useQueryClient();
  const key = ["onboarding-progress", flow, entityId ?? null];
  const { data, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => fetchProgress(flow, entityId ?? null),
  });

  const save = useMutation({
    mutationFn: async (input: {
      step: number;
      total: number;
      state: Record<string, unknown>;
      completed?: boolean;
      entity_id?: string | null;
    }) => {
      const { data, error } = await (supabase.rpc as unknown as (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: unknown; error: { message: string } | null }>)(
        "mark_onboarding_step",
        {
          _flow: flow,
          _step: input.step,
          _total: input.total,
          _state: input.state,
          _entity: input.entity_id ?? entityId ?? null,
          _completed: !!input.completed,
        },
      );
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  return { data, isLoading, save };
}

/** Autosaves step + state whenever they change; debounced. */
export function useAutosaveOnboarding(
  flow: OnboardingFlow,
  step: number,
  total: number,
  state: Record<string, unknown>,
  opts?: { entityId?: string | null; enabled?: boolean; delayMs?: number },
) {
  const { save } = useOnboardingProgress(flow, opts?.entityId ?? null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enabled = opts?.enabled !== false;
  useEffect(() => {
    if (!enabled) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      save.mutate({ step, total, state, entity_id: opts?.entityId ?? null });
    }, opts?.delayMs ?? 800);
    return () => { if (timer.current) clearTimeout(timer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, JSON.stringify(state), total, enabled]);
}
